import React, { useState } from "react";
import { motion } from "framer-motion";
import { MailCheck, Send, LoaderCircle } from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "./Reveal";
import { api, formatApiError } from "../lib/api";

export const EmailCapture = ({ data }) => {
    const [email, setEmail] = useState("");
    const [status, setStatus] = useState("idle");

    const submit = async (e) => {
        e.preventDefault();
        if (status === "loading") return;
        setStatus("loading");
        try {
            await api.post("/subscribe", { email });
            setStatus("success");
            toast.success(data.success_message);
        } catch (err) {
            setStatus("idle");
            toast.error(formatApiError(err, "Please check your email address."));
        }
    };

    return (
        <section id="checklist" className="py-16 sm:py-20">
            <Reveal>
                <div className="grain relative overflow-hidden rounded-[20px] bg-[#222522] px-7 py-12 sm:px-12 text-center">
                    <div className="pointer-events-none absolute -top-24 left-1/2 h-64 w-64 -translate-x-1/2 rounded-full bg-[#576856]/30 blur-3xl" />
                    <h2 className="relative font-serif text-2xl sm:text-3xl font-semibold tracking-tight text-[#FDFCF8]">{data.title}</h2>
                    <p className="relative mx-auto mt-4 max-w-[440px] text-sm leading-relaxed text-[#FDFCF8]/70">{data.text}</p>

                    {status === "success" ? (
                        <>
                        <motion.div
                            data-testid="email-success-message"
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                            className="relative mx-auto mt-8 flex max-w-[440px] items-center justify-center gap-2.5 rounded-full border border-[#5A7A5A]/50 bg-[#5A7A5A]/20 px-6 py-3.5 text-sm font-semibold text-[#DDE5DB]"
                        >
                            <MailCheck size={17} strokeWidth={1.8} />
                            {data.success_message}
                        </motion.div>
                        <p className="relative mt-4 text-xs text-[#FDFCF8]/50">Check your inbox — the printable checklist PDF is on its way.</p>
                        </>
                    ) : (
                        <form onSubmit={submit} className="relative mx-auto mt-8 flex max-w-[440px] flex-col gap-3 sm:flex-row">
                            <input
                                data-testid="email-input"
                                type="email"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="you@example.com"
                                className="h-12 flex-1 rounded-full border border-white/15 bg-white/10 px-5 text-sm text-[#FDFCF8] placeholder:text-[#FDFCF8]/40 outline-none backdrop-blur-sm transition-colors focus:border-[#9AB098]"
                            />
                            <button
                                data-testid="email-submit-btn"
                                type="submit"
                                disabled={status === "loading"}
                                className="flex h-12 items-center justify-center gap-2 rounded-full bg-[#9AB098] px-7 text-sm font-bold text-[#222522] transition-all duration-300 hover:bg-[#DDE5DB] active:scale-[0.98] disabled:opacity-70"
                            >
                                {status === "loading" ? <LoaderCircle size={16} className="animate-spin" /> : <Send size={15} strokeWidth={1.8} />}
                                {data.button_label}
                            </button>
                        </form>
                    )}
                </div>
            </Reveal>
        </section>
    );
};
